import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import MuiTextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RestoreIcon from '@material-ui/icons/Restore';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import BackButton from 'src/components/BackButton';
import Button from 'src/components/Button';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { ConfirmDialogRef } from 'src/components/ConfirmDialog/interfaces';
import InputFile from 'src/components/Form/InputFile';
import TextField from 'src/components/Form/TextField';
import { IEditParams } from 'src/interfaces/IEditParams';
import IVoucherStamp from 'src/interfaces/models/IVoucherStamp';
import PrivateContext from 'src/routes/Private/PrivateContext';
import api from 'src/services/api';
import { handleApiResponseErrors, showFormErrors } from 'src/utils/errors';
import { objectToFormData } from 'src/utils/helpers';
import yupValidate from 'src/utils/yupValidate';
import {
  VoucherStampStoreSchema,
  VoucherStampUpdateSchema,
} from 'src/validators/VoucherStamp/save.schema';

import { Container } from './styles';

const Save: FC = () => {
  const { startLayoutLoading, stopLayoutLoading } = useContext(PrivateContext);
  const formRef = useRef<FormHandles>(null);
  const confirmDialogRef = useRef<ConfirmDialogRef>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [partnersCount, setPartnersCount] = useState(0);
  const [activeVoucherStamps, setActiveVoucherStamps] = useState<
    IVoucherStamp[]
  >([]);
  const [replacementVoucherStampId, setReplacementVoucherStampId] = useState<
    number | ''
  >('');
  const history = useHistory();
  const params = useParams<IEditParams>();

  const yupSchema = params.id
    ? VoucherStampUpdateSchema
    : VoucherStampStoreSchema;

  useEffect(() => {
    const loadData = async () => {
      try {
        startLayoutLoading();

        if (params.id) {
          const response = await api.get(`/admin/voucher-stamps/${params.id}`);
          const voucherStamp = response.data as IVoucherStamp;
          const voucherStampsResponse = await api.get<IVoucherStamp[]>(
            '/admin/voucher-stamps',
          );

          setStatus(voucherStamp.status);
          setPartnersCount(voucherStamp.partners_count || 0);
          setActiveVoucherStamps(voucherStampsResponse.data);
          formRef.current?.setData({
            ...voucherStamp,
            file: voucherStamp.asset_path,
          });
        }
      } catch (error) {
        handleApiResponseErrors(error.response, 'Erro ao buscar dados.');
      } finally {
        stopLayoutLoading();
      }
    };

    loadData();
  }, [params.id, startLayoutLoading, stopLayoutLoading]);

  const handleOnSubmit: SubmitHandler = async (unformData) => {
    try {
      setLoading(true);
      formRef.current?.setErrors({});

      const { success, data, errors } = await yupValidate(
        yupSchema,
        unformData,
      );

      if (!success) {
        return showFormErrors(errors, formRef);
      }

      const formData = objectToFormData(data);

      if (params.id) {
        await api.put(`/admin/voucher-stamps/${params.id}`, formData);
      } else {
        await api.post('/admin/voucher-stamps', formData);
      }

      toast.success('Dados salvos com sucesso!');
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro', yupSchema, formRef);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      if (status === 'active') {
        await api.delete(`/admin/voucher-stamps/${params.id}`, {
          data:
            partnersCount > 0
              ? {
                  replacement_voucher_stamp_id: replacementVoucherStampId,
                }
              : undefined,
        });
        toast.success('Carimbo desativado!');
      } else {
        await api.post(`/admin/voucher-stamps/${params.id}/restore`);
        toast.success('Carimbo restaurado!');
      }

      setDeactivateDialogOpen(false);
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao atualizar carimbo.');
    } finally {
      setLoading(false);
    }
  };

  const replacementOptions = activeVoucherStamps.filter(
    (voucherStamp) => voucherStamp.id !== Number(params.id),
  );

  return (
    <Container maxWidth="md">
      <Form ref={formRef} onSubmit={handleOnSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <BackButton />

              <Typography variant="h5">
                {params.id ? 'Editar' : 'Novo'} Carimbo de Voucher
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <InputFile name="file" label="Imagem do Carimbo" required />
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField name="label" label="Nome" required />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField name="cnpj" label="CNPJ" required />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Grid container justify="space-between">
              {status === 'active' ? (
                <Button
                  type="button"
                  loading={loading}
                  startIcon={<CloseIcon />}
                  variant="contained"
                  color="secondary"
                  disabled={!params.id}
                  onClick={() => {
                    setReplacementVoucherStampId(
                      replacementOptions[0]?.id || '',
                    );
                    setDeactivateDialogOpen(true);
                  }}
                >
                  Desativar
                </Button>
              ) : (
                <Button
                  type="button"
                  loading={loading}
                  startIcon={<RestoreIcon />}
                  color="primary"
                  disabled={!params.id}
                  onClick={() => confirmDialogRef.current?.show()}
                >
                  Restaurar
                </Button>
              )}

              <Button
                type="submit"
                loading={loading}
                startIcon={<CheckIcon />}
                variant="contained"
                color="primary"
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Form>

      <Dialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Desativar carimbo</DialogTitle>

        <DialogContent>
          {partnersCount > 0 ? (
            <>
              <DialogContentText variant="body1">
                Este carimbo está vinculado a {partnersCount} parceira(s).
                Selecione outro carimbo ativo para substituir esse vínculo antes
                da inativação.
              </DialogContentText>

              <MuiTextField
                select
                variant="outlined"
                label="Novo carimbo das parceiras"
                fullWidth
                size="small"
                margin="dense"
                value={replacementVoucherStampId}
                onChange={(event) =>
                  setReplacementVoucherStampId(Number(event.target.value))
                }
                helperText={
                  replacementOptions.length
                    ? 'Seleção obrigatória para concluir a inativação.'
                    : 'Nenhum outro carimbo ativo disponível no momento.'
                }
              >
                {replacementOptions.map((voucherStamp) => (
                  <MenuItem key={voucherStamp.id} value={voucherStamp.id}>
                    {voucherStamp.label}
                  </MenuItem>
                ))}
              </MuiTextField>
            </>
          ) : (
            <DialogContentText variant="body1">
              Confirma a inativação deste carimbo?
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            onClick={() => setDeactivateDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            loading={loading}
            startIcon={<CloseIcon />}
            variant="contained"
            color="secondary"
            onClick={handleConfirm}
            disabled={
              partnersCount > 0 &&
              (!replacementVoucherStampId || !replacementOptions.length)
            }
          >
            Desativar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        ref={confirmDialogRef}
        title="Restaurar carimbo"
        description="Confirma esta ação?"
        confirmColor="primary"
        onConfirm={handleConfirm}
      />
    </Container>
  );
};

export default Save;
